/**
 * CREATIVE DETONATOR - Centralized API Endpoints
 * v1 - Single source of truth for all API URLs
 *
 * Usage:
 *   import { API_BASE, ENDPOINTS, apiUrl } from './endpoints.js';
 *   const url = apiUrl(ENDPOINTS.ideas.list);
 *   const url = apiUrl(ENDPOINTS.ideas.update(ideaId));
 */

// ============================================================================
// BASE URL CONFIGURATION
// ============================================================================

/**
 * Get API base URL based on environment
 * @returns {string} - Base URL for API calls
 */
export function getApiBase() {
  // Check for explicit override
  if (window.API_CONFIG_URL) {
    return window.API_CONFIG_URL;
  }

  // Auto-detect based on hostname
  const hostname = window.location.hostname;
  if (hostname === '127.0.0.1' || hostname === 'localhost') {
    return 'http://localhost:3000';
  }

  // Production: cross-origin API
  return 'https://api.creativedetonator.com';
}

// Export the base URL for use in other modules
export const API_BASE = getApiBase();

// ============================================================================
// ENDPOINT DEFINITIONS
// ============================================================================

export const ENDPOINTS = {
  // === Generation & AI ===
  generate: '/api/generate',
  generateStream: '/api/generate-stream',
  refineIdea: '/api/refine-idea',
  generateVideoScenes: '/api/generate-video-scenes',
  generateSceneFrame: '/api/generate-scene-frame',

  // === Configuration ===
  status: '/api/status',
  config: '/api/config',
  triggers: '/api/triggers',
  trigger: (code) => `/api/triggers/${encodeURIComponent(code)}`,
  storageStatus: '/api/storage/status',

  // === Image Generation ===
  imageGen: {
    providers: '/api/image-gen/providers',
    generate: '/api/image-gen/generate',
    submit: '/api/image-gen/submit',
    status: (modelId, requestId) =>
      `/api/image-gen/status/${encodeURIComponent(modelId)}/${encodeURIComponent(requestId)}`,
    result: (modelId, requestId) =>
      `/api/image-gen/result/${encodeURIComponent(modelId)}/${encodeURIComponent(requestId)}`,
    buildPrompt: '/api/image-gen/build-prompt',
    edit: '/api/image-gen/edit',
    editSubmit: '/api/image-gen/edit/submit',
  },

  // === Video Generation ===
  videoGen: {
    config: '/api/video-gen/config',
    submit: '/api/video-gen/submit',
    status: (modelId, requestId) =>
      `/api/video-gen/status/${encodeURIComponent(modelId)}/${encodeURIComponent(requestId)}`,
    result: (modelId, requestId) =>
      `/api/video-gen/result/${encodeURIComponent(modelId)}/${encodeURIComponent(requestId)}`,
    buildPrompt: '/api/video-gen/build-prompt',
    transferStatus: '/api/video-gen/transfer-status',
  },

  // === Ideas ===
  ideas: {
    list: '/api/ideas',
    save: '/api/ideas/save',
    delete: (id) => `/api/ideas/${encodeURIComponent(id)}`,
    update: (id) => `/api/ideas/${encodeURIComponent(id)}`,
    versions: (id) => `/api/ideas/${encodeURIComponent(id)}/versions`,
    restore: (id, version) => `/api/ideas/${encodeURIComponent(id)}/restore/${version}`,
    deleteVersion: (id, version) => `/api/ideas/${encodeURIComponent(id)}/versions/${version}`,
    duplicateVersion: (id, version) => `/api/ideas/${encodeURIComponent(id)}/versions/${version}/duplicate`,
    updateImage: (id) => `/api/ideas/${encodeURIComponent(id)}/image`,
    updateVideo: (id) => `/api/ideas/${encodeURIComponent(id)}/video`,
    updateSceneVideo: (id) => `/api/ideas/${encodeURIComponent(id)}/scene-video`,
  },

  // === User & Credits ===
  user: {
    me: '/api/me',
    updateProfile: '/api/me',
    avatar: '/api/me/avatar',
    export: '/api/me/export',
    delete: '/api/me',
    preferences: '/api/preferences',
    credits: '/api/credits',
    creditsHistory: '/api/credits/history',
  },

  // === Sessions ===
  sessions: {
    list: '/api/sessions',
    get: (id) => `/api/sessions/${encodeURIComponent(id)}`,
  },

  // === Media ===
  media: {
    images: '/api/images',
    videos: '/api/videos',
    exportSession: '/api/export-session',
    exportSessionPdf: '/api/export-session-pdf',
  },

  // === Storyboard ===
  storyboard: {
    split: '/api/storyboard/split',
  },

  // === Storage ===
  storage: {
    upload: '/api/upload',
    uploadUrl: '/api/storage/upload-url',
    delete: '/api/storage/delete',
  },

  // === Brands ===
  brands: {
    list: '/api/brands',
    create: '/api/brands',
    update: (id) => `/api/brands/${encodeURIComponent(id)}`,
    delete: (id) => `/api/brands/${encodeURIComponent(id)}`,
    synthesize: '/api/brands/synthesize',
  },
};

// ============================================================================
// URL BUILDER HELPERS
// ============================================================================

/**
 * Build full API URL from endpoint path
 * @param {string} endpoint - Endpoint path (from ENDPOINTS or custom)
 * @returns {string} - Full URL with base
 */
export function apiUrl(endpoint) {
  return `${API_BASE}${endpoint}`;
}

/**
 * Build URL with query parameters
 * @param {string} endpoint - Endpoint path
 * @param {Object} params - Query parameters object
 * @returns {string} - Full URL with query string
 */
export function apiUrlWithParams(endpoint, params = {}) {
  const baseUrl = apiUrl(endpoint);
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

// ============================================================================
// LEGACY SUPPORT
// ============================================================================

// For backward compatibility with code that expects API_CONFIG.baseUrl
export const API_CONFIG = {
  baseUrl: API_BASE,
};

// SECURITY: Global window exposure removed to prevent DOM clobbering attacks
// All code should import these values using ES modules
